import _getAppName from "../functions/helpers/getAppName.js";
import _ssaFunc from "../functions/profiles/subStationAlpha.js";
import _eol from "eol";
import _replaceColorTagsAss from "../functions/quill/replaceColorTagsAss.js";
import _replaceUnderlineTags from "../functions/quill/replaceUnderlineTags.js";
import _replaceItalicTags from "../functions/quill/replaceItalicTags.js";
import _replaceBoldTags from "../functions/quill/replaceBoldTags.js";
import _convertToPlainText from "../functions/quill/convertToPlainText.js";
import _convertToHtml from "../functions/quill/convertToHtml.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
import _Event from "../classes/event.js";
const Event = _Event;
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const convertToHtml = _convertToHtml;
const convertToPlainText = _convertToPlainText;
const replaceBoldTags = _replaceBoldTags;
const replaceItalicTags = _replaceItalicTags;
const replaceUnderlineTags = _replaceUnderlineTags;
const replaceColorTagsAss = _replaceColorTagsAss;
const eol = _eol;
const ssaFunc = _ssaFunc;
const getAppName = _getAppName;
function parseFinite(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
function assTimestamp(seconds, options) {
  let timestamp = tcLib.secToTcMs(seconds, options.frameRate, options.dropFrame);
  timestamp = timestamp.replace(/^0/, "").replace(",", ".");
  return timestamp.replace(/\.(\d{3})$/, (match, ms) => {
    const cs = Math.round(parseInt(ms) / 10);
    return `.${cs.toString().padStart(2, "0")}`;
  });
}
function getEventPosition(event, options, videoHeight) {
  const windowSettings = options.window || {};
  const windowHeightRaw = parseFinite(windowSettings.height);
  const windowWidthRaw = parseFinite(windowSettings.width);
  const windowHeight = windowHeightRaw && windowHeightRaw > 0 ? windowHeightRaw : 15;
  const windowWidth = windowWidthRaw && windowWidthRaw > 0 ? windowWidthRaw : null;
  const paddingPercentRaw = parseFinite(windowSettings.yPadding);
  const yPaddingPercent = Math.max(0, paddingPercentRaw ?? 0);
  let yPos = "end";
  let xPos = "center";
  let marginL = "0000";
  let marginR = "0000";
  let marginV = "0000";
  const numericYPos = parseFinite(event.yPos);
  if (typeof event.yPos === "string") {
    const normalizedY = event.yPos.toLowerCase();
    if (["start", "center", "end"].includes(normalizedY)) {
      yPos = normalizedY;
    }
  } else if (numericYPos !== null) {
    if (numericYPos >= windowHeight) {
      yPos = "end";
      marginV = "0000";
    } else {
      const safeLineOffset = Math.max(0, numericYPos);
      const offsetPercent = windowHeight > 0 ? safeLineOffset / (windowHeight + 1) * 100 : 0;
      const effectiveVideoHeight = videoHeight - videoHeight * (yPaddingPercent / 100);
      const marginPixels = Math.round(offsetPercent / 100 * effectiveVideoHeight);
      marginV = Math.min(9999, Math.max(0, marginPixels)).toString().padStart(4, "0");
      yPos = "start";
    }
  } else if (windowSettings.height) {
    const fallbackY = parseFinite(windowSettings.height) ? parseFinite(event.yPos) : null;
    if (fallbackY !== null) {
      const yPercent = fallbackY / windowHeight * 100;
      if (yPercent <= 40) {
        yPos = "start";
      } else if (yPercent > 40 && yPercent <= 60) {
        yPos = "center";
      } else {
        yPos = "end";
      }
    }
  }
  const numericXPos = parseFinite(event.xPos);
  if (typeof event.xPos === "string") {
    const normalizedX = event.xPos.toLowerCase();
    if (["start", "center", "end"].includes(normalizedX)) {
      xPos = normalizedX;
    }
  } else if (numericXPos !== null && windowWidth) {
    const offsetPercent = Math.abs(numericXPos) / windowWidth * 100;
    if (offsetPercent > 50) {
      xPos = "end";
    } else if (offsetPercent > 20) {
      xPos = "center";
    } else {
      xPos = "start";
    }
  } else if (numericXPos !== null && windowSettings.width) {
    const xPercent = numericXPos / windowSettings.width * 100;
    if (xPercent <= 33) {
      xPos = "start";
    } else if (xPercent > 33 && xPercent <= 66) {
      xPos = "center";
    } else {
      xPos = "end";
    }
  }
  return {
    eventStyle: ssaFunc.styleMap[yPos][xPos],
    marginL,
    marginR,
    marginV
  };
}
function formatAssText(text) {
  let assText = replaceColorTagsAss(text);
  assText = replaceBoldTags(assText, "{\\b1}", "{\\b0}");
  assText = replaceItalicTags(assText, "{\\i1}", "{\\i0}");
  assText = replaceUnderlineTags(assText, "{\\u1}", "{\\u0}");
  assText = convertToPlainText(assText);
  return ssaFunc.formatText(assText);
}
function createDialogue(context, text, startTime = context.startTime, endTime = context.endTime) {
  return eol.after(`Dialogue: 0,${startTime},${endTime},${context.eventStyle},${context.speaker},${context.marginL},${context.marginR},${context.marginV},,${text}`);
}
function isRollUp(style) {
  return style === "Roll-Up 2" || style === "Roll-Up 3" || style === "Roll-Up 4";
}
function getRollUpLineCount(style) {
  switch (style) {
    case "Roll-Up 2":
      return 2;
    case "Roll-Up 3":
      return 3;
    case "Roll-Up 4":
      return 4;
    default:
      return 1;
  }
}
function getRollUpText(events, currentIndex) {
  const currentEvent = events[currentIndex];
  if (!currentEvent || !isRollUp(currentEvent.style)) {
    return currentEvent?.text || "";
  }
  const maxLines = getRollUpLineCount(currentEvent.style);
  let collected = [];
  for (let i = currentIndex; i >= 0 && collected.length < maxLines; i--) {
    const event = events[i];
    if (!isRollUp(event.style) || !convertToPlainText(event.text)) {
      break;
    }
    collected.unshift(event.text);
  }
  return collected.join("");
}
function getRollUpEnd(event, nextEvent) {
  if (nextEvent && nextEvent.start !== false && nextEvent.start > event.start) {
    return nextEvent.start;
  }
  return event.end;
}
function getVisibleLength(tokens) {
  return tokens.reduce((length, token) => {
    if (/^\{\\[^}]*\}$/.test(token)) {
      return length;
    }
    return length + 1;
  }, 0);
}
function getPaintOnTokens(text) {
  return text.match(/\{\\[^}]*\}|\\N|[\s\S]/g) || [];
}
function getPaintOnText(tokens, visibleLimit) {
  let visibleCount = 0;
  let output = "";
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (/^\{\\[^}]*\}$/.test(token)) {
      output += token;
      continue;
    }
    if (visibleCount >= visibleLimit) {
      break;
    }
    output += token;
    visibleCount += 1;
  }
  return output;
}
export default {
  decode: function (input, options) {
    let events = [],
      styleMap = {};
    let decodingOptions = getFormatOptions(options.formatOptions);
    let tcPattern = new RegExp(/\d{1}:\d{2}:\d{2}.\d{1,2}/g);
    let stylePattern = new RegExp(/^Style: /gi);
    const fileLines = eol.split(input).filter(fileLine => {
      return fileLine;
    });
    let playResX = input.match(/PlayResX:\s*(\d+)/);
    let playResY = input.match(/PlayResY:\s*(\d+)/);
    if (playResX && playResX.length > 1) {
      playResX = parseInt(playResX[1]);
    } else {
      playResX = 1280;
    }
    if (playResY && playResY.length > 1) {
      playResY = parseInt(playResY[1]);
    } else {
      playResY = 720;
    }

    //console.log(playResX, playResY);

    let excludedStyleTypes = decodingOptions["Excluded Style Types"] ? decodingOptions["Excluded Style Types"].split(",") : [];
    let includedStyleTypes = decodingOptions["Included Style Types"] ? decodingOptions["Included Style Types"].split(",") : [];
    let enableIncludedStyleTypes = decodingOptions["Enable Included Style Types"] === "yes" ? true : false;
    let useRegexForExcludedStyles = decodingOptions["Use Regex for Excluded Style Types"] === "yes" ? true : false;
    let useRegexForIncludedStyles = decodingOptions["Use Regex for Included Style Types"] === "yes" ? true : false;
    fileLines.forEach(fileLine => {
      let matches = fileLine.match(tcPattern);
      let styleMatches = fileLine.match(stylePattern);
      if (matches && matches.length >= 2) {
        let parts = fileLine.split(",");
        let styleName = parts[3].trim();
        styleName = styleName ? styleName.toLowerCase() : "";
        let style = styleMap[styleName];
        // console.log("-------------");
        // console.log(styleName,": ", style);

        parts.splice(0, 9);
        let ccEvent = new Event();
        ccEvent.start = tcLib.centisecToSeconds(matches[0]);
        ccEvent.end = tcLib.centisecToSeconds(matches[1]);
        let text = parts.join(",");
        //console.log(`Before: ${text}`);
        if (useRegexForExcludedStyles) {
          if (ssaFunc.checkValueInRegexArray(styleName, excludedStyleTypes)) {
            return;
          }
        } else {
          if (excludedStyleTypes.includes(styleName)) {
            return;
          }
        }
        if (useRegexForIncludedStyles && enableIncludedStyleTypes) {
          if (!ssaFunc.checkValueInRegexArray(styleName, includedStyleTypes)) {
            return;
          }
        } else if (enableIncludedStyleTypes && !includedStyleTypes.includes(styleName)) {
          return;
        }
        if (style && style.alignment) {
          if (ssaFunc.alignmentMap[style.alignment]) {
            ccEvent.xPos = ssaFunc.alignmentMap[style.alignment].xPos;
            ccEvent.yPos = ssaFunc.alignmentMap[style.alignment].yPos;
            ccEvent.alignment = ssaFunc.alignmentMap[style.alignment].alignment;
          }
        }

        /* Margin based positioning START */
        let vOffset = style && style.marginV ? parseInt(style.marginV) / playResY * 100 : 0;
        // let leftOffset = style && style.marginL  ? (parseInt(style.marginL)/playResX) * 100 : 0;
        // let rightOffset = style && style.marginR ? (parseInt(style.marginR)/playResX) * 100 : 0;

        /* 
            We now need to apply the vertical, left, and right margin offsets based on the alignment value (style.alignment). 
            In SubStation Alpha (SSA/ASS) subtitle files, the margins work together with the alignment to define where subtitles appear.
              How Margins Work
                The margins define boundary distances from the edges of the video frame:
                  MarginL: Distance in pixels from the left edge of the video
                MarginR: Distance in pixels from the right edge of the video
                MarginV: Distance in pixels from either the top or bottom edge (depending on alignment)
              How Alignment Uses the Margins:
                The alignment value (1-9, like a numpad) then positions the subtitle relative to these margins:
                Horizontal positioning:
                  Left-aligned (1, 4, 7): The subtitle's left edge starts at MarginL
                Center-aligned (2, 5, 8): The subtitle is centered between MarginL and MarginR
                Right-aligned (3, 6, 9): The subtitle's right edge ends at MarginR from the right edge
                  Vertical positioning:
                  Bottom-aligned (1, 2, 3): The subtitle sits MarginV pixels up from the bottom
                Middle-aligned (4, 5, 6): The subtitle is vertically centered (MarginV is usually ignored)
                Top-aligned (7, 8, 9): The subtitle sits MarginV pixels down from the top
              To calculate the Event's yPos we'll have to look at three scenarios:
                1. Bottom-aligned (ccEvent.yPos === "end"): The subtitle sits MarginV pixels up from the bottom
                2. Middle-aligned (ccEvent.yPos === "center"): The subtitle is vertically centered (MarginV is usually ignored and we can just leave the yPos as center)
                3. Top-aligned (ccEvent.yPos === "start"): The subtitle sits MarginV pixels down from the top
                  We then need to calculate the total offset based on the vOffset percentage calculated above and the windows yPadding. To calculate the total offset we'll need to subtract the vOffset from the window Offset to get the difference (vOffset - options.window.yPadding). If the value is <= 0 then we'll leave the yPos as a flexbox value. If the value is > 0 we'll need to convert the percentage difference to lines and apply that to the event's yPos. For example, if the window padding is 10% and the vOffset is 15% we'll have a difference of 5%. We then need to take 5% of the windows height to calculate the number of lines. 5% of 15 (options.window.height) is ~0.75 or 1 when rounded. This means we'll need to take the events starting position 1 when aligned at the top of the screen, and 15-total number of character lines when aligned near the bottom. We then need to add or subtract 1 line. For Events that are top aligned, the yPos value would be 2. For 2 line Events at the bottom of the screen it would be (15-(2-1))-1 = 13.
            
            SPECIAL NOTE REGARDING XPOS - we will not be calculating the margin offset for XPOS at this time. We'll only be applying vOffsets to yPos due to some complexity.                     
        */

        /* Calculate YPOS based on vertical margin overflow */
        const windowSettings = options && options.window ? options.window : {};
        const rawWindowHeight = windowSettings.height ?? 15;
        const parsedWindowHeight = typeof rawWindowHeight === "number" ? rawWindowHeight : parseFloat(rawWindowHeight);
        const windowHeight = Math.max(1, Number.isFinite(parsedWindowHeight) ? parsedWindowHeight : 15);
        const rawWindowPadding = windowSettings.yPadding ?? 10;
        const parsedWindowPadding = typeof rawWindowPadding === "number" ? rawWindowPadding : parseFloat(rawWindowPadding);
        const windowPadding = Math.max(0, Number.isFinite(parsedWindowPadding) ? parsedWindowPadding : 10);
        const marginOverflowPercent = Math.max(0, vOffset - windowPadding);
        const totalLineCount = Math.max(1, (text || "").split(/\\N/g).length);
        const maxStartLine = Math.max(1, windowHeight - (totalLineCount - 1));
        const lineShift = marginOverflowPercent > 0 ? Math.max(1, Math.round(marginOverflowPercent / 100 * windowHeight)) : 0;
        if (ccEvent.yPos === "start") {
          if (lineShift > 0) {
            const adjustedLine = Math.min(maxStartLine, 1 + lineShift);
            ccEvent.yPos = adjustedLine;
          }
        } else if (ccEvent.yPos === "end") {
          if (lineShift > 0) {
            const baseLine = maxStartLine;
            const adjustedLine = Math.max(1, baseLine - lineShift);
            ccEvent.yPos = adjustedLine;
          }
        } else {
          ccEvent.yPos = "center";
        }

        /* Margin Based Positioning END */

        //Apply styling based on style map
        if (style && style.underline != 0) {
          text = `{\\u1}` + text.split(/\\N/g).join(`{\\u0}\\N{\\u1}`) + `{\\u0}`;
        }
        if (style && style.italic != 0) {
          text = `{\\i1}` + text.split(/\\N/g).join(`{\\i0}\\N{\\i1}`) + `{\\i0}`;
        }
        if (style && style.bold != 0) {
          text = `{\\b1}` + text.split(/\\N/g).join(`{\\b0}\\N{\\b1}`) + `{\\b0}`;
        }
        if (style && style.primaryColorValid !== "white") {
          text = `{\\c&${style.primaryColor}&}` + text.split(/\\N/g).join(`{\\c&${style.primaryColor}&}\\N{\\c&${style.primaryColor}&}`) + `{\\c&${style.primaryColor}&}`;
        }

        //console.log(`During: ${text}`);

        // let subtitlePosition = ssaFunc.getPositionFromSubtitle(text);
        // ccEvent.xPos = subtitlePosition.x;
        // ccEvent.yPos = subtitlePosition.y;

        // if (ccEvent.yPos === "end"){
        //     ccEvent.yOffset = options.window.height*-0.10;
        // } else if (ccEvent.yPos === "start"){
        //     ccEvent.yOffset = options.window.height*0.10;
        // }

        text = ssaFunc.parseCmdCodes(ccEvent, text, options, playResX, playResY);
        ccEvent.text = convertToHtml(text);
        events.push(ccEvent);
      } else if (styleMatches) {
        styleMap = ssaFunc.parseStyles(fileLine, styleMap);
      }
    });
    return events;
  },
  encode: function (eventGroup, options) {
    let encodingOptions = getFormatOptions(options.formatOptions);
    // console.log(JSON.stringify(encodingOptions, null, 4));

    let output = "",
      borderStyle = encodingOptions['Border Style'] || "3",
      fontFamily = encodingOptions['Font Family'] || "monospace",
      fontSize = encodingOptions['Font Size'] || 23,
      fontColor = encodingOptions['Font Color'] || "#FFFFFF",
      opacity = encodingOptions['Opacity'] ?? 100,
      spacing = encodingOptions['Letter Spacing'] || "0",
      shadow = encodingOptions['Shadow Size (px)'] || "0",
      shadowColor = encodingOptions['Shadow Color'] || "#000000",
      shadowOpacity = encodingOptions['Shadow Opacity'] ?? 100,
      outline = encodingOptions['Outline Size (px)'] || 2,
      outlineColorRaw = encodingOptions['Outline Color'] || "#000000",
      outlineOpacity = encodingOptions['Outline Opacity'] ?? 100,
      videoWidth = encodingOptions['Video Width'] || 1280,
      videoHeight = encodingOptions['Video Height'] || 720,
      scaleX = encodingOptions['ScaleX'] || 100,
      scaleY = encodingOptions['ScaleY'] || 100,
      marginX = encodingOptions['Margin X'] ?? Math.round(videoWidth * (Math.max(0, parseFinite(options.window?.xPadding) ?? 10) / 100)),
      marginY = encodingOptions['Margin Y'] ?? Math.round(videoHeight * (Math.max(0, parseFinite(options.window?.yPadding) ?? 10) / 100)),
      boldAll = encodingOptions['Bold All'] === "yes" ? true : false,
      italicsAll = encodingOptions['Italics All'] === "yes" ? true : false,
      underlineAll = encodingOptions['Underline All'] === "yes" ? true : false,
      strikeoutAll = encodingOptions['Strikeout All'] === "yes" ? true : false;
    let primaryColor = ssaFunc.encodeColor(fontColor, opacity),
      secondaryColor = ssaFunc.encodeColor(fontColor, opacity),
      outlineColor = ssaFunc.encodeColor(outlineColorRaw, outlineOpacity),
      backgroundColor = ssaFunc.encodeColor(shadowColor, shadowOpacity);
    marginX = marginX.toString().padStart(4, '0');
    marginY = marginY.toString().padStart(4, '0');
    output += eol.after("[Script Info]");
    output += eol.after("; Script generated by " + getAppName() + " | www.closedcaptionconverter.com");
    output += eol.after("ScriptType: v4.00+");
    output += eol.after("Collisions: Normal");
    output += eol.after("PlayDepth: 0");
    output += eol.after(`PlayResX: ${videoWidth}`);
    output += eol.after(`PlayResY: ${videoHeight}`);
    output += String(eol.auto);
    output += eol.after("[V4+ Styles]");
    output += eol.after("Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding");
    output += eol.after(`Style: Default, Arial, 24,&H00B4FCFC,&H00B4FCFC,&H00000008,&H80000008,0,0,0,0,100,100,0,0,1,1,2,2,0,0,0,1`);
    output += eol.after(`Style: TopLeft, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},7,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: TopCenter, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},8,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: TopRight, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},9,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: CenterLeft, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},4,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: CenterCenter, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},5,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: CenterRight, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},6,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: BottomLeft, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},1,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: BottomCenter, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},2,${marginX},${marginX},${marginY},1`);
    output += eol.after(`Style: BottomRight, ${fontFamily},${fontSize},${primaryColor},${secondaryColor},${outlineColor},${backgroundColor},${boldAll ? '-1' : '0'},${italicsAll ? '-1' : '0'},${underlineAll ? '-1' : '0'},${strikeoutAll ? '-1' : '0'},${scaleX},${scaleY},${spacing},0,${borderStyle},${outline},${shadow},3,${marginX},${marginX},${marginY},1`);
    output += String(eol.auto);
    output += eol.after("[Events]");
    output += eol.after("Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text");
    eventGroup.events.forEach((event, index, events) => {
      // Add error handling for required properties
      if (!event || event.start === false || !event.end || !event.text) {
        return; // Skip invalid events
      }
      const position = getEventPosition(event, options, videoHeight);
      const context = {
        ...position,
        startTime: assTimestamp(event.start, options),
        endTime: assTimestamp(event.end, options),
        speaker: event.speaker && event.speaker.name ? event.speaker.name : ""
      };
      if (isRollUp(event.style)) {
        const end = getRollUpEnd(event, events[index + 1]);
        const text = formatAssText(getRollUpText(events, index));
        output += createDialogue(context, text, context.startTime, assTimestamp(end, options));
        return;
      }
      const text = formatAssText(event.text);
      if (event.style === "Paint-On") {
        const tokens = getPaintOnTokens(text);
        const visibleLength = getVisibleLength(tokens);
        if (visibleLength <= 1) {
          output += createDialogue(context, text);
          return;
        }
        const duration = event.end - event.start;
        if (duration <= 0) {
          return;
        }
        const step = duration / visibleLength;
        for (let visibleCount = 1; visibleCount <= visibleLength; visibleCount++) {
          const segmentStart = event.start + step * (visibleCount - 1);
          const segmentEnd = visibleCount === visibleLength ? event.end : event.start + step * visibleCount;
          const partialText = getPaintOnText(tokens, visibleCount);
          output += createDialogue(context, partialText, assTimestamp(segmentStart, options), assTimestamp(segmentEnd, options));
        }
        return;
      }
      output += createDialogue(context, text);
    });
    return output;
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return input;
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      return eventGroup;
    }
  }
};